import React, { useContext, useEffect, useState } from "react";
import RequireAuth from "../../shared/components/guards/RequireAuth";
import { AuthContext } from "../../contexts/AuthContext";
import { PageTitle } from "../../shared/components/styled/Typography.styled";
import ActionSnackbar from "../../shared/components/ActionSnackbar";
import { NO_LOAN_REGISTERED_WARNING_MESSAGE } from "../../shared/constants/UserProfile.constants";
import { Box, Paper, Tooltip, useTheme, useMediaQuery, Button } from "@mui/material";
import {
  AsideNavigation,
  FlexContainer,
  RelativeContainer,
} from "../../shared/components/styled/Container.styled";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Statements from "./statements/Statements";
import {
  BANNER_IMAGE_STATEMENTS_KEY,
  BANNER_URL_STATEMENTS_KEY,
  DOCUMENTS_ALLOWED_TO_BE_DOWNLOADED_KEY,
  DOCUMENTS_ALLOW_ACCESS_KEY,
  DOCUMENTS_ALLOW_REQUEST_PAYOFF_KEY,
  DOCUMENTS_ALLOW_UPLOAD_KEY,
} from "../../shared/constants/Configurator.constants";
import MarketingBanner from "../../shared/components/configurator/MarketingBanner";
import LoanDetailsExpandable from "../../shared/components/LoanDetailsExpandable";
import { ConfiguratorContext } from "../../contexts/ConfiguratorContext";
import { VIEW_DOCUMENT_NOT_ALLOWED_MESSAGE } from "../../shared/constants/StatementsDocuments.constants";
import {
  DOCUMENT_VIEW_CATEGORY_OTHER,
  DOCUMENT_VIEW_CATEGORY_STATEMENTS,
  DOCUMENT_VIEW_CATEGORY_TAX,
} from "../../shared/constants/StatementsDocuments.constants";
import { getVisibleDocumentViewCategories } from "../../shared/utils/documentViewSelections";
import { findInArray, isAllowed } from "../../shared/utils/utility-functions";
import { getBorrowerUploadCategories } from "../../shared/utils/documentUploadSelections";
import { isLoanClosedToPayments } from "../../shared/utils/payment-functions";
import { useResponsivePadding } from "../../shared/hooks/useResponsivePadding";
import PageLayout from "../../shared/components/PageLayout";
import HorizontalMobileNavigation from "../../shared/components/HorizontalMobileNavigation";

const StatementsAndDocuments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeLoan, activeLoanDetails, token } = useContext(AuthContext);
  const { edittedFields } = useContext(ConfiguratorContext);
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [noLoanSnackbarOpen, setNoLoanSnackbarOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { bannerContainer, contentContainer } = useResponsivePadding();
  // Server-resolved rule (US-1062443) with raw-indicator fallback for mocks.
  const inActiveOrDeadLoan = isLoanClosedToPayments(
    activeLoanDetails?.loanDetails,
  );
  const configuratorDocumentsAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_ACCESS_KEY
  );
  const configuratorDocumentsUploadAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_UPLOAD_KEY
  );
  const configuratorPayoffQuoteAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_REQUEST_PAYOFF_KEY
  );

  const canViewDocuments = isAllowed(
    configuratorDocumentsAllowField,
    "value",
    "true",
    true
  );
  // AC6: an upload is offered only while the client allows it and has named something to file
  // under. Naming nothing hides the section, as it already does for viewable documents.
  const canUploadDocuments =
    isAllowed(configuratorDocumentsUploadAllowField, "value", "true", true) &&
    getBorrowerUploadCategories(edittedFields).length > 0;
  const canPayoffQuote = isAllowed(
    configuratorPayoffQuoteAllowField,
    "value",
    "true",
    true
  );
  const documentDownloadConfigField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOWED_TO_BE_DOWNLOADED_KEY,
  );
  // Which document pages are offered follows from what the client named for viewing alone.
  // Allowing an upload grants no read-back: XDoc keeps one container per loan and does not
  // record who filed a document, so a page offered off the back of an upload category would
  // show the borrower whatever the client filed under it too (pending US-1027823 product item 2).
  const visibleDocumentCategories = getVisibleDocumentViewCategories(
    documentDownloadConfigField?.value || "",
  );
  const showStatementsCategory =
    canViewDocuments &&
    visibleDocumentCategories[DOCUMENT_VIEW_CATEGORY_STATEMENTS];
  const showTaxCategory =
    canViewDocuments && visibleDocumentCategories[DOCUMENT_VIEW_CATEGORY_TAX];
  const showOtherCategory =
    canViewDocuments && visibleDocumentCategories[DOCUMENT_VIEW_CATEGORY_OTHER];

  const getFirstVisibleDocumentPath = () => {
    if (showStatementsCategory) {
      return "/statements-documents";
    }
    if (showTaxCategory) {
      return "/statements-documents/tax";
    }
    if (showOtherCategory) {
      return "/statements-documents/other";
    }
    if (canUploadDocuments) {
      return "/statements-documents/upload";
    }
    if (canPayoffQuote) {
      return "/statements-documents/request";
    }
    return "/";
  };

  const isStatementsPath =
    location.pathname === "/statements-documents" ||
    location.pathname === "/statements-documents/";
  const isTaxPath = location.pathname.includes("/statements-documents/tax");
  const isOtherPath = location.pathname.includes("/statements-documents/other");

  useEffect(() => {
    if (
      !canViewDocuments &&
      (location.pathname === "/statements-documents" ||
        location.pathname === "/statements-documents/")
    ) {
      if (canUploadDocuments) {
        navigate("/statements-documents/upload", { replace: true });
      } else if (canPayoffQuote) {
        navigate("/statements-documents/request", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewDocuments, canUploadDocuments, canPayoffQuote, location]);

  useEffect(() => {
    // US-1147260: block direct URL access to the payoff request page when the
    // admin has disabled payoff requests - redirect to the first available page.
    if (
      !canPayoffQuote &&
      location.pathname.includes("/statements-documents/request")
    ) {
      navigate(getFirstVisibleDocumentPath(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPayoffQuote, location]);

  useEffect(() => {
    if (!canViewDocuments) {
      return;
    }

    const isHiddenCategoryPath =
      (isStatementsPath && !showStatementsCategory) ||
      (isTaxPath && !showTaxCategory) ||
      (isOtherPath && !showOtherCategory);

    if (isHiddenCategoryPath) {
      navigate(getFirstVisibleDocumentPath(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canViewDocuments,
    isStatementsPath,
    isTaxPath,
    isOtherPath,
    showStatementsCategory,
    showTaxCategory,
    showOtherCategory,
    location.pathname,
  ]);

  useEffect(() => {
    if (token && activeLoan === "no-loan") {
      setNoLoanSnackbarOpen(true);
    } else {
      setNoLoanSnackbarOpen(false);
    }
  }, [token, activeLoan]);

  const handleNoLoanSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setNoLoanSnackbarOpen(false);
  };

  return (
    <RequireAuth>
      <RelativeContainer width="100%">
        <PageTitle
          variant="h1"
          component="h1"
          color="primary.dark"
          fontSize="3rem"
          display={isMobile ? "none" : "block"}
          sx={{
            width: {
              md: 'calc(100% - 24px - 500px)',
              lg: '100%',
            }
          }}
        >
          Statements & Documents
        </PageTitle>
        {!isMobile && <LoanDetailsExpandable />}
      </RelativeContainer>
      <Paper
        elevation={0}
        sx={{ backgroundColor: { xs: "unset", md: "background.paper" } }}
      >
        <PageLayout>
          <FlexContainer
            justifycontent="space-between"
            alignitems="center"
            width="100%"
            sx={{ marginBottom: isMobile ? "20px" : 0 }}
          >
            <PageTitle
              variant="h1"
              component="h1"
              color="primary.black"
              fontSize="2rem"
              display={isMobile ? "block" : "none"}
              padding="0 !important"
              // width="20%"
            >
              Statements & Documents
            </PageTitle>

            {isMobile && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowLoanDetails(!showLoanDetails)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  borderColor: "primary.main",
                  color: "primary.main",
                  fontSize: "0.875rem",
                  padding: "8px 16px",
                  minWidth: "140px",
                  "&:hover": {
                    borderColor: "primary.dark",
                    color: "primary.dark",
                  },
                }}
              >
                {showLoanDetails ? "Hide Details" : "View Loan Details"}
              </Button>
            )}
          </FlexContainer>

            {isMobile && showLoanDetails && (
            <Box sx={{ 
              marginBottom: "20px",
              backgroundColor: "background.paper",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}>
              <LoanDetailsExpandable expanded={true} />
            </Box>
          )}

          <FlexContainer
            alignitems="flex-start"
            sx={{ flexDirection: { xs: "column", md: "row" } }}
          >
            {isMobile ? (
              <HorizontalMobileNavigation
                items={[
                  ...(showStatementsCategory
                    ? [
                        {
                          to: "/statements-documents",
                          label: "Statements",
                        },
                      ]
                    : []),
                  ...(showTaxCategory
                    ? [
                        {
                          to: "tax",
                          label: "Tax Documents",
                        },
                      ]
                    : []),
                  ...(showOtherCategory
                    ? [
                        {
                          to: "other",
                          label: "Other Documents",
                        },
                      ]
                    : []),
                  ...(!inActiveOrDeadLoan && canPayoffQuote
                    ? [
                        {
                          to: "request",
                          label: "Request Payoff",
                          linkSx: {
                            fontWeight: 700,
                          },
                        },
                      ]
                    : []),
                  ...(!inActiveOrDeadLoan && canUploadDocuments
                    ? [
                        {
                          to: "upload",
                          label: "Upload Documents",
                        },
                      ]
                    : []),
                ]}
              />
            ) : (
              // Desktop vertical navigation
              <AsideNavigation>
                {showStatementsCategory ? (
                  <Tooltip
                    title={
                      !canViewDocuments ? VIEW_DOCUMENT_NOT_ALLOWED_MESSAGE : ""
                    }
                  >
                    <Box
                      sx={{
                        cursor: !canViewDocuments ? "not-allowed" : "inherit",
                        lineHeight: 1.286,
                      }}
                    >
                      <Link
                        to="/statements-documents"
                        className={
                          !canViewDocuments
                            ? "disabled"
                            : isStatementsPath
                            ? "active"
                            : ""
                        }
                      >
                        Statements
                      </Link>
                    </Box>
                  </Tooltip>
                ) : null}
                {showTaxCategory ? (
                  <Tooltip
                    title={
                      !canViewDocuments ? VIEW_DOCUMENT_NOT_ALLOWED_MESSAGE : ""
                    }
                  >
                    <Box
                      sx={{
                        cursor: !canViewDocuments ? "not-allowed" : "inherit",
                        lineHeight: 1.286,
                      }}
                    >
                      <Link
                        to="tax"
                        className={
                          !canViewDocuments
                            ? "disabled"
                            : isTaxPath
                            ? "active"
                            : ""
                        }
                      >
                        Tax Documents
                      </Link>
                    </Box>
                  </Tooltip>
                ) : null}
                {showOtherCategory ? (
                  <Tooltip
                    title={
                      !canViewDocuments ? VIEW_DOCUMENT_NOT_ALLOWED_MESSAGE : ""
                    }
                  >
                    <Box
                      sx={{
                        cursor: !canViewDocuments ? "not-allowed" : "inherit",
                        lineHeight: 1.286,
                      }}
                    >
                      <Link
                        to="other"
                        className={
                          !canViewDocuments
                            ? "disabled"
                            : isOtherPath
                            ? "active"
                            : ""
                        }
                      >
                        Other Documents
                      </Link>
                    </Box>
                  </Tooltip>
                ) : null}
                {!inActiveOrDeadLoan && canPayoffQuote ? (
                  <Box
                    sx={{
                      lineHeight: 1.286,
                    }}
                  >
                    <Link
                      to="request"
                      className={
                        location.pathname.includes(
                          "/statements-documents/request"
                        )
                          ? "active"
                          : ""
                      }
                      style={{ fontWeight: 700 }}
                    >
                      Request Payoff Quote
                    </Link>
                  </Box>
                ) : null}
                {!inActiveOrDeadLoan && canUploadDocuments ? (
                  <Box
                    sx={{
                      lineHeight: 1.286,
                    }}
                  >
                    <Link
                      to="upload"
                      className={
                        location.pathname.includes(
                          "/statements-documents/upload"
                        )
                          ? "active"
                          : ""
                      }
                    >
                      Upload Documents
                    </Link>
                  </Box>
                ) : null}
              </AsideNavigation>
            )}
            <FlexContainer padding={contentContainer} width="100%">
              <FlexContainer width="100%" flexdirection="column" gap="25px">
                {isStatementsPath && showStatementsCategory ? (
                  <Statements />
                ) : (
                  <Outlet />
                )}
              </FlexContainer>
            </FlexContainer>
          </FlexContainer>
        </PageLayout>
        <FlexContainer padding={bannerContainer} alignitems="flex-start">
          <MarketingBanner
            elementKey={BANNER_IMAGE_STATEMENTS_KEY}
            hyperlinkElementKey={BANNER_URL_STATEMENTS_KEY}
          />
        </FlexContainer>
      </Paper>
      <ActionSnackbar
        severity="warning"
        borderColor={theme.palette.warning.main}
        vertical="top"
        horizontal="center"
        width="50%"
        isOpen={noLoanSnackbarOpen}
        closeFn={handleNoLoanSnackbarClose}
        snackbarLabel={NO_LOAN_REGISTERED_WARNING_MESSAGE}
        actionLabel="Register Loan"
        actionFn={() => navigate("/profile/loan")}
      />
    </RequireAuth>
  );
};

export default StatementsAndDocuments;